// Mapeo de la paleta de branding del partner → variables de tema del widget
// Line V2, para que el checkout herede el estilo activo.
//
// Desde 2026-07-31 el widget pinta el DOCUMENTO entero: `backgroundColor` se
// mapea a `--background` y `--card` y el CSS de Line hace
// `body{background-color:var(--background)}` (pedido en
// docs/payments/pedido-line-theming-fondo.md, deployado por Line a staging y
// prod). Por eso mandamos la paleta ACTIVA del portal — la oscura cuando el
// portal está en oscuro — y el iframe se funde con la página en ambos modos.
// Ojo: el widget saltea `backgroundColor: 'transparent'` por diseño; siempre
// hay que mandar un color concreto.

import type { BrandingPalette } from '@/lib/api';

// Luminancia relativa (WCAG) de un color hex #rgb / #rrggbb. null si no parsea.
function hexLuminance(color: string): number | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  const raw = m?.[1];
  if (!raw) return null;
  const h = raw.length === 3
    ? raw.split('').map((c) => c + c).join('')
    : raw;
  const chan = (i: number): number => parseInt(h.slice(i, i + 2), 16) / 255;
  const lin = (c: number): number => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(chan(0)) + 0.7152 * lin(chan(2)) + 0.0722 * lin(chan(4));
}

// Un color hex oscuro (no parseable → lo tratamos como claro para no romper).
function isDark(color: string): boolean {
  const l = hexLuminance(color);
  return l !== null && l < 0.5;
}

// Variables de tema del widget Line (camelCase, se pasan como query params).
//
// Recibe la paleta ACTIVA del portal (la que resuelve el ThemeProvider según
// el color scheme — `resolvePalette`), no siempre la clara.
//
// - `backgroundColor` = fondo de PÁGINA del portal (no `surface`): el widget
//   pinta body y tarjeta con el mismo valor, y lo que queremos es que el
//   rectángulo del iframe desaparezca contra la página. Las tarjetas internas
//   quedan delimitadas por el borde.
// - `borderColor` solo sobre fondo oscuro: el borde gris claro default del
//   widget está pensado para fondo blanco y resulta chillón en oscuro. En
//   claro lo omitimos y queda el default, que ya estaba bien.
//
// NO mandamos `fontFamily`: el widget no carga webfonts (no tiene @font-face ni
// Google Fonts) y es un iframe cross-origin, así que no puede usar la Inter que
// carga el portal. Si mandáramos el stack de marca `'Inter','Georgia',serif`,
// caería a Georgia (serif) — inconsistente con el portal. Sin el parámetro, el
// widget usa su default `system-ui` (sans), que queda parejo con el portal.
// Volveremos a mandar la fuente cuando Line exponga un `fontUrl`.
export function paletteToLineTheme(palette: BrandingPalette): Record<string, string> {
  return {
    primaryColor: palette.primary,
    backgroundColor: palette.background,
    textColor: palette.text,
    ...(isDark(palette.background) ? { borderColor: 'rgba(255,255,255,0.16)' } : {}),
    borderRadius: '12',
    buttonBorderRadius: '10',
  };
}

// Origen esperado del postMessage del widget, derivado de la checkout_url que
// devuelve el backend. La config del provider (staging vs prod) vive
// server-side, así que la URL es la fuente de verdad — no depender de una env
// var de build por entorno (el default estático a staging hacía que el bundle
// prod descartara los eventos del widget productivo). El env var queda solo
// como fallback si la URL no parsea.
const FALLBACK_WIDGET_ORIGIN =
  (import.meta.env.VITE_LINE_WIDGET_ORIGIN as string | undefined) ??
  'https://staging.checkout.line.net.ar';

export function lineWidgetOrigin(checkoutUrl: string): string {
  try {
    return new URL(checkoutUrl).origin;
  } catch {
    return FALLBACK_WIDGET_ORIGIN;
  }
}

// Appendea (o sobreescribe) los params de tema a la checkout_url del widget.
// Si la URL no parsea, devuelve la original sin tocar.
export function withLineTheme(checkoutUrl: string, theme: Record<string, string>): string {
  try {
    const url = new URL(checkoutUrl);
    for (const [key, value] of Object.entries(theme)) {
      if (value) url.searchParams.set(key, value);
    }
    return url.toString();
  } catch {
    return checkoutUrl;
  }
}
